import CloseIcon from '@mui/icons-material/Close';
// @mui material components
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { Card, CardContent, Dialog, DialogContent, DialogTitle, Grid, IconButton, Typography, Fade } from '@mui/material';
import { Box } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import Loading from 'components/Loading/Loading.js';
import MDBox from 'components/MDBox';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { getCavHoursForSelectedTimeRange } from 'helpers/kwh';
import { calculateKwhWastePotentialBaselineForSelectedRange } from 'helpers/helpers';
import DashboardLayout from 'layouts/LayoutContainers/DashboardLayout';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import {
  clearControllerData,
  fetchCurrentData,
  //   fetchEvents,
  fetchControllerRangeData,
  setControllerChartPopUp,
  setControllerLoading,
  setControllerTimeRangeValue,
} from 'stores/sensor/controllersSlice';
import { TimeInMS } from 'types/time';
import Charts, { getControllerChartDataset } from './Charts.js';
import FormattedChart from './components/FormattedChart';
import GraphsFilterBar from './components/GraphsFilterBar';
import KwhInfo from './components/KwhInfo.js';
import TopBar from './components/TopBar.js';
import { CurrentData } from './CurrentData';
import { ControllerCurrentDataFields } from 'types/ControllerFields.js';

/**
 * The refresh rate that symbolize that auto refresh is off
 */
const REFRESH_RATE_OFF = 0;

// last 12 months
const TimeRangeOptions = [
  // { label: "1 Minute", value: TimeInMS.Minute },
  { label: '1 Hour', value: TimeInMS.Hour },
  { label: 'Today', value: TimeInMS.Day },
  { label: '1 Week', value: TimeInMS.Week },
  { label: '1 Month', value: TimeInMS.Month },
  { label: 'date to date', value: 'date to date' },
];
//#endregion

function ControllerPage() {
  // const { auth } = useAuth();
  // let refreshRate = refreshRateOptions[0];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const controller_id = useParams().id;
  const selectedGraphs = useSelector(state => state.controllers.selectedGraphs);
  // const currentUser = useSelector(state => state.sensors.currentUser);
  const data = useSelector(state => state.controllers.data);
  const controllerModel = useSelector(state => state.controllers.currentMeasurements?.controller_model);
  const deviceType = useSelector(state => state.controllers.currentMeasurements?.device_type);
  const device_mac = useSelector(state => state.controllers.device_mac, shallowEqual);
  const energyInfo = useSelector(state => state.controllers.energyInfo);
  // const firstUpdate = useSelector(state => state.controllers.firstUpdate);
  const chartPopUp = useSelector(state => state.controllers.chartPopUp);
  const loading = useSelector(state => state.controllers.loading);
  const rangeObj = useSelector(state => state.controllers.timeRange);
  const controllerName = useSelector(state => state.controllers.data.controllerName);
  const skipNextRangeRefreshRef = useRef(false);
  const [unitMeta, setUnitMeta] = useState({
    unit_type: null,
    cooling_type: null,
    heating_type: null,
    area_m2: null,
    area_cfm: null,
    area_fan_kw: null,
    mode: null,
  });
  const [visible, setVisible] = useState(false);
  const [mobileChartsOpen, setMobileChartsOpen] = useState(false);
  const [mobileChartsView, setMobileChartsView] = useState('filters');
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const selectedGraphsKey = useMemo(() => selectedGraphs.join('|'), [selectedGraphs]);
  const rangeValue = rangeObj?.value;
  const hasRangeMeasurements = data.rangeMeasurements && Object.keys(data.rangeMeasurements).length !== 0;

  useEffect(() => {
    if (!controller_id || !deviceType) {
      return;
    }

    if (skipNextRangeRefreshRef.current) {
      skipNextRangeRefreshRef.current = false;
      return;
    }
    updateRangeData();
    // getUpdateLastUpdateTime();
  }, [rangeValue, selectedGraphsKey, deviceType]);

  const refreshAllNoGraphs = () => {
    // updateCurrentData();
    // updateAlerts();
    getUpdateLastUpdateTime();
  };

  const changeTimeRange = range => {
    // console.log("changeTimeRange triggered");
    dispatch(setControllerTimeRangeValue(range));
  };

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 2000);
    return () => {
      dispatch(clearControllerData());
      clearTimeout(t);
    };
  }, []);

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getChartQuickRange = rangeKey => {
    if (rangeKey === 'yesterday') {
      const yesterday = DateTime.now().minus({ days: 1 });
      const yesterdayValue = yesterday.toFormat('yyyy-MM-dd');

      return {
        label: 'Yesterday',
        value: `${yesterdayValue}_${yesterdayValue}`,
      };
    }

    if (rangeKey === 'week') {
      return { label: '1 Week', value: TimeInMS.Week };
    }

    if (rangeKey === 'last3Hours') {
      return { label: 'Last 3 Hours', value: TimeInMS.Hour * 3 };
    }

    if (rangeKey === 'last6Hours') {
      return { label: 'Last 6 Hours', value: TimeInMS.Hour * 6 };
    }

    if (rangeKey === 'last12Hours') {
      return { label: 'Last 12 Hours', value: TimeInMS.Hour * 12 };
    }

    if (rangeKey === 'last2Days') {
      return { label: 'Last 2 Days', value: TimeInMS.Day * 2 };
    }

    return { label: 'Today', value: TimeInMS.Day };
  };

  const chartRangeButtons = [
    { label: 'Last 3 Hours', rangeKey: 'last3Hours', isActive: rangeObj.label === 'Last 3 Hours' },
    { label: 'Last 6 Hours', rangeKey: 'last6Hours', isActive: rangeObj.label === 'Last 6 Hours' },
    { label: 'Last 12 Hours', rangeKey: 'last12Hours', isActive: rangeObj.label === 'Last 12 Hours' },
    { label: 'Today', rangeKey: 'today', isActive: rangeObj.label === 'Today' },
    { label: 'Yesterday', rangeKey: 'yesterday', isActive: rangeObj.label === 'Yesterday' },
    { label: 'Last 2 Days', rangeKey: 'last2Days', isActive: rangeObj.label === 'Last 2 Days' },
    { label: '1 Week', rangeKey: 'week', isActive: rangeObj.label === '1 Week' },
  ];

  const handleChartRangeSelect = button => {
    const range = getChartQuickRange(button.rangeKey);

    skipNextRangeRefreshRef.current = true;
    dispatch(setControllerTimeRangeValue(range));
    updateRangeData(range.value);
  };

  const updateRangeData = async (timeRangeValue = rangeObj.value) => {
    try {
      const payload = await dispatch(
        fetchControllerRangeData({
          measurements: selectedGraphs,
          controller_id: controller_id,
          timeRange: timeRangeValue,
          userTimeZone: userTimeZone,
        }),
      ).unwrap();

      setUnitMeta({
        unit_type: payload?.data.unit_type ?? null,
        cooling_type: payload?.data.cooling_type ?? null,
        heating_type: payload?.data.heating_type ?? null,
        area_m2: payload?.data.area_m2 ?? null,
        area_cfm: payload?.data.area_cfm ?? null,
        area_fan_kw: payload?.data.area_fan_kw ?? null,
        mode: payload?.data.mode ?? null,
      });
    } catch (error) {
      if (error?.statusCode === 403) {
        navigate('/unauthorized', { replace: true });
        return;
      }
      console.error('Failed to fetch controller range data:', error);
    }
  };

  useEffect(() => {
    dispatch(setControllerLoading(false));
    const refreshAllNoGraphsId = setInterval(refreshAllNoGraphs, 360000);
    const updateRangeDataId = setInterval(updateRangeData, 900000);

    return () => {
      dispatch(clearControllerData());
      // Clear any old refresh rate
      clearInterval(refreshAllNoGraphsId);
      clearInterval(updateRangeDataId);
    };
  }, []);

  const getUpdateLastUpdateTime = () => {
    if ((data !== undefined && data.lastUpdate > 0) || data.lastUpdate === 0) {
      const topBar = document.getElementById('controllerPageTopBar');
      if (topBar) {
        return new Date(data.lastUpdate * 1000).toLocaleString('en-GB');
      }
    }

    // return new Date().toLocaleString("en-GB");
    return 'N/A';
  };

  const toDdMmYyyy = date =>
    `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
  const getSelectedTimeRangeForBaseline = selectedRange => {
    if (selectedRange && typeof selectedRange === 'object' && selectedRange.startDate && selectedRange.endDate) {
      return selectedRange;
    }

    if (typeof selectedRange === 'string' && selectedRange.includes('_')) {
      const [startStr, endStr] = selectedRange.split('_');
      const startDate = new Date(startStr);
      const endDate = new Date(endStr);
      if (!isNaN(startDate) && !isNaN(endDate)) {
        return {
          startDate: toDdMmYyyy(startDate),
          endDate: toDdMmYyyy(endDate),
        };
      }
      return {};
    }

    if (typeof selectedRange === 'number') {
      const now = new Date();
      const ONE_DAY_MS = 24 * 60 * 60 * 1000;
      const getDayStart = date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
      let rangeStart = new Date(now.getTime() - selectedRange);

      if (selectedRange >= TimeInMS.Day) {
        const isWeek = selectedRange === TimeInMS.Week;
        const startOffsetMs = isWeek ? selectedRange : selectedRange - ONE_DAY_MS;
        rangeStart = getDayStart(new Date(now.getTime() - startOffsetMs));
      }

      return {
        startDate: toDdMmYyyy(rangeStart),
        endDate: toDdMmYyyy(now),
      };
    }

    return {};
  };

  const controllerParams = {
    area_m2: unitMeta.area_m2,
    area_cfm: unitMeta.area_cfm,
    unit_type: unitMeta.unit_type,
    area_fan_kw: unitMeta.area_fan_kw,
    cooling_type: unitMeta.cooling_type,
    heating_type: unitMeta.heating_type,
    mode: unitMeta.mode ?? 1,
  };
  const selectedCavActiveHours = {
    cav_start_time: energyInfo.cavStartTime || {},
    cav_end_time: energyInfo.cavEndTime || {},
  };
  const totalCavNumOfHours = getCavHoursForSelectedTimeRange(rangeObj.value, energyInfo.cavStartTime, energyInfo.cavEndTime);
  const selectedTimeRangeForBaseline = getSelectedTimeRangeForBaseline(rangeObj.value);
  const calculatedKwhWastePotential = calculateKwhWastePotentialBaselineForSelectedRange(
    selectedCavActiveHours,
    selectedTimeRangeForBaseline,
    controllerParams,
  );
  const displayedKwhWastePotential = Number.isFinite(calculatedKwhWastePotential)
    ? calculatedKwhWastePotential
    : energyInfo.kwhWastePotentialSum;

  return (
    <DashboardLayout>
      <DashboardNavbar objectName={controllerName} />
      <Typography
        fontSize="11px"
        sx={{
          paddingLeft: { xs: 0, sm: '12px' },
          marginTop: { xs: '-15px', sm: '-5px' },
          textAlign: { xs: 'center', sm: 'left' },
        }}>
        Smart indoor air monitoring & management platform
      </Typography>
      <Dialog open={chartPopUp !== undefined} fullWidth maxWidth="xl" sx={{ zIndex: 100000 }}>
        <DialogTitle sx={{ padding: '0' }}>
          <IconButton
            aria-label="close"
            // onClick={() => setChartPopUp(undefined)}
            onClick={() => dispatch(setControllerChartPopUp(undefined))}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: '50px' }}>
          {chartPopUp ? (
            <FormattedChart
              title={chartPopUp?.title}
              datasets={Object.keys(data.rangeMeasurements).map(name =>
                getControllerChartDataset(data.rangeMeasurements[name], name),
              )}
              isRange={chartPopUp?.isRange}
              setChartPopUp={setControllerChartPopUp}
              isPopUp={true}
              controller_id={controller_id}
              onChartRangeSelect={handleChartRangeSelect}
              chartRangeButtons={chartRangeButtons}
            />
          ) : undefined}
        </DialogContent>
      </Dialog>
      <Dialog
        open={mobileChartsOpen && isSmallScreen}
        onClose={() => setMobileChartsOpen(false)}
        fullScreen
        sx={{
          zIndex: 100000,
          width: '100vw',
          height: '100vh',
          '& .MuiDialog-paper': {
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            maxHeight: '100vh',
            m: 0,
            overflow: 'hidden',
            backgroundColor: '#fff',
          },
        }}>
        <DialogTitle sx={{ p: 0, position: 'absolute', top: 0, left: 0, zIndex: 1 }}>
          <IconButton aria-label="close charts" onClick={() => setMobileChartsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            overflow: 'hidden',
            ...(mobileChartsView === 'charts'
              ? {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  flex: 'none',
                  '@media (orientation: portrait)': {
                    width: '100dvh',
                    height: '100dvw',
                    transform: 'rotate(90deg) translateY(-100%)',
                    transformOrigin: 'top left',
                  },
                  '@media (orientation: landscape)': {
                    width: '100dvw',
                    height: '100dvh',
                  },
                }
              : {
                  width: '100dvw',
                  height: '100dvh',
                }),
          }}>
          <Box
            sx={{
              width: '100%',
              height: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              p: mobileChartsView === 'charts' ? 0.5 : 1,
              pt: mobileChartsView === 'charts' ? 0.5 : 4,
              '& > .MuiFormGroup-root': {
                flex: '1 1 auto',
                minHeight: 0,
                overflow: 'hidden',
              },
              '& .MuiFormControlLabel-root': {
                m: 0,
                mr: mobileChartsView === 'filters' ? 0 : 1,
              },
              '& .MuiFormControlLabel-label': {
                fontSize: '.72rem',
              },
              '& .chart-container': {
                flex: '1 1 auto',
                minHeight: 0,
                height: '100%',
                width: '100%',
                overflow: 'hidden',
                p: 0,
                m: 0,
              },
              '& .MuiCard-root': {
                borderRadius: mobileChartsView === 'charts' ? 0 : undefined,
                boxShadow: mobileChartsView === 'charts' ? 'none' : undefined,
              },
              '& .solin-line-chart': {
                overflow: 'hidden',
              },
            }}>
            {mobileChartsView === 'filters' ? (
              <GraphsFilterBar
                hasRadar={data.hasRadar}
                compact
                showRefresh={false}
                onConfirm={() => setMobileChartsView('charts')}
              />
            ) : (
              hasRangeMeasurements && (
                <Charts compact onChartRangeSelect={handleChartRangeSelect} chartRangeButtons={chartRangeButtons} />
              )
            )}
          </Box>
        </DialogContent>
      </Dialog>
      <MDBox pt={1} pb={1}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <TopBar
              onOpenCharts={() => {
                setMobileChartsView('filters');
                setMobileChartsOpen(true);
              }}
            />
            <Box
              container
              sx={{
                overflowX: 'visible',
                overflowY: 'visible',
                display: 'flex',
                // height: '100vh',
                paddingBottom: '5px',
                flexDirection: {
                  xs: 'column',
                  lg: 'row',
                },
                justifyContent: 'space-between',
              }}
              className="bodycontainer">
              <Box
                className="curDataContainer"
                item
                xs={12}
                md={2}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: {
                    xs: 'center',
                    lg: 'flex-start',
                  },
                  width: {
                    xs: '100%',
                    lg: 'auto',
                  },
                  flexShrink: 0,
                }}>
                <CurrentData
                  controller_id={controller_id}
                  device_mac={device_mac}
                  controller_model={controllerModel}
                  cav_start_time={energyInfo.cavStartTime || {}}
                  cav_end_time={energyInfo.cavEndTime || {}}
                  title="Control panel & real time data"
                />
              </Box>
              <Box
                item
                justifyContent="space-between"
                paddingTop={1}
                className="ChartsBox"
                xs={10}
                sx={{
                  padding: {
                    xs: '8px 0',
                    lg: '0px 3px 0px 20px',
                  },
                  marginTop: {
                    xs: '2rem',
                    lg: '0',
                  },
                  flexGrow: '1',
                  width: {
                    xs: '100%',
                    lg: 'auto',
                  },
                  minWidth: '0',
                }}>
                {!isSmallScreen && hasRangeMeasurements && <GraphsFilterBar hasRadar={data.hasRadar} />}
                {hasRangeMeasurements ? (
                  <>
                    {!isSmallScreen && (
                      <Charts onChartRangeSelect={handleChartRangeSelect} chartRangeButtons={chartRangeButtons} />
                    )}
                    <KwhInfo
                      hvacOnCount={energyInfo.hvacUnitOnCount}
                      coolWaterOpenCount={energyInfo.coolWaterOpenCount}
                      kwhTotalWaste={energyInfo.kwhSum}
                      kwhWastePotential={displayedKwhWastePotential}
                      avgCoolWaterValveWhenActive={energyInfo.avgCoolWaterValveWhenActive}
                      timeRangeInUnix={rangeObj.value}
                      loading={false}
                      unitType={unitMeta.unit_type}
                      coolingType={unitMeta.cooling_type}
                      heatingType={unitMeta.heating_type}
                      // NumOfCavHoursPerDay={}
                      totalCavNumOfHours={totalCavNumOfHours}
                      totalCo2Emissions={energyInfo.co2Emissions}
                      // timeRangeLabel={`${from.toFormat('dd.LL.yyyy HH:mm')} – ${to.toFormat('dd.LL.yyyy HH:mm')}`}
                    />
                  </>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}>
                    {!loading && (
                      <Fade in={visible} timeout={400} unmountOnExit>
                        <Card
                          variant="outlined"
                          sx={{
                            borderColor: 'grey.300',
                            borderRadius: 2,
                            p: 2,
                            maxWidth: 400,
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'grey.50',
                          }}>
                          <QueryStatsIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                          <CardContent sx={{ p: 0 }}>
                            <Typography variant="subtitle1" color="text.primary" fontWeight="bold">
                              No Data Available
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              No data found for the selected controller in this time range. Try adjusting the filters or selecting
                              a different range.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Fade>
                    )}
                  </Box>
                )}
                {loading && (
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}>
                    <Box
                      sx={{
                        // height: "80%",
                        display: 'flex',
                        justifyContent: 'center',
                      }}>
                      <Loading />
                    </Box>
                  </Box>
                )}
                {/* <Box sx={{}}>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      justifyContent: 'space-between',
                      marginTop: '1rem',
                    }}>
                    <AlarmsAndFaults />
                  </Box>
                </Box> */}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default ControllerPage;
